import type React from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';

import { TextButton } from '@xing-com/button';
import { FabiJobSearchQueryGenerationUseCase } from '@xing-com/crate-common-graphql-types';
import { navigate } from '@xing-com/crate-core-router';
import { AI_SEARCH_INIT_PROMPT_PARAM } from '@xing-com/crate-jobs-constants';
import { ROUTES } from '@xing-com/crate-jobs-paths';
import { useJobsNwtTracking } from '@xing-com/crate-jobs-tracking';
import {
  IconAIProfileSearch,
  IconAICareerChanger,
  IconSalary,
} from '@xing-com/icons';
import { mediaConfined, mediaTopBarVisible } from '@xing-com/layout-tokens';
import { Marker } from '@xing-com/marker';
import { spaceXL, spaceM, spaceXS } from '@xing-com/tokens';

import { Tag } from './promotions.styles';
import { SearchAlertPromotions } from './search-alert-promotions';

type PromotionProps = {
  Icon: React.FC<React.SVGProps<SVGSVGElement>>;
  title: string;
  kind: FabiJobSearchQueryGenerationUseCase;
  element: string;
  showNewMarker?: boolean;
  showPromotionsAsButtons?: boolean;
};

const Promotion: React.FC<PromotionProps> = ({
  title,
  kind,
  Icon,
  element,
  showNewMarker,
  showPromotionsAsButtons,
}) => {
  const trackNwt = useJobsNwtTracking();

  const onClick = (): void => {
    trackNwt({
      event: 'opened',
      element,
      elementDetail: 'pill',
      componentName: 'ai_search',
      eventSchema: 'aicomponent',
      page: 'jobs/index',
    });

    const params = new URLSearchParams({ [AI_SEARCH_INIT_PROMPT_PARAM]: kind });
    navigate(`${ROUTES.searchAi}?${params.toString()}`);
  };

  const Content = (
    <>
      <FormattedMessage id={title} />
      {showNewMarker ? (
        <Marker variant="ai" aria-hidden="true">
          <FormattedMessage id="XDS_FLAG_NEW_NEUTRAL" />
        </Marker>
      ) : null}
    </>
  );

  if (showPromotionsAsButtons) {
    return (
      <PromotionButton icon={Icon} onClick={onClick} size="small">
        {Content}
      </PromotionButton>
    );
  }

  return (
    <Tag leadingIcon={Icon} hideBorder onClick={onClick}>
      {Content}
    </Tag>
  );
};

const SWIMP_PROMOTION = (showPromotionsAsButtons?: boolean): JSX.Element => (
  <Promotion
    Icon={IconAIProfileSearch}
    title="JOBS_SEARCH_AI_SWIMP_SUGGESTION"
    kind={FabiJobSearchQueryGenerationUseCase.SearchWithProfile}
    element="search_with_my_profile"
    showPromotionsAsButtons={showPromotionsAsButtons}
  />
);
const SWIMS_PROMOTION = (showPromotionsAsButtons?: boolean): JSX.Element => (
  <Promotion
    Icon={IconAICareerChanger}
    title="JOBS_SEARCH_AI_SWIMS_SUGGESTION"
    kind={FabiJobSearchQueryGenerationUseCase.SearchWithSkills}
    element="search_with_my_skills"
    showPromotionsAsButtons={showPromotionsAsButtons}
  />
);
const SALARY_PROMOTION = (showPromotionsAsButtons?: boolean): JSX.Element => (
  <Promotion
    Icon={IconSalary}
    title="JOBS_SEARCH_AI_SALARY_SUGGESTION"
    kind={FabiJobSearchQueryGenerationUseCase.EarnMore}
    element="want_to_earn_more"
    showNewMarker
    showPromotionsAsButtons={showPromotionsAsButtons}
  />
);

type Props = {
  isSearchAndShuffle?: boolean;
  showPromotionsAsButtons?: boolean;
};

export const Promotions: React.FC<Props> = ({
  isSearchAndShuffle,
  showPromotionsAsButtons,
}) => {
  return (
    <Container
      $isSearchAndShuffle={isSearchAndShuffle}
      $showPromotionsAsButtons={showPromotionsAsButtons}
    >
      {SALARY_PROMOTION(showPromotionsAsButtons)}
      {SWIMP_PROMOTION(showPromotionsAsButtons)}
      {SWIMS_PROMOTION(showPromotionsAsButtons)}
      {isSearchAndShuffle && (
        <SearchAlertPromotions
          showPromotionsAsButtons={showPromotionsAsButtons}
        />
      )}
    </Container>
  );
};

const Container = styled.div<{
  $isSearchAndShuffle?: boolean;
  $showPromotionsAsButtons?: boolean;
}>`
  display: flex;
  gap: ${({ $showPromotionsAsButtons }) =>
    $showPromotionsAsButtons ? spaceXL : spaceM};
  margin-top: 20px;
  padding: ${spaceXS} 20px;
  overflow: scroll;
  scrollbar-width: none;

  @media ${mediaConfined} {
    justify-content: center;
  }

  ${({ $isSearchAndShuffle }) =>
    $isSearchAndShuffle &&
    css`
      margin-top: ${spaceM};

      @media ${mediaTopBarVisible} {
        margin-top: ${spaceM};
      }
    `};
`;

const PromotionButton = styled(TextButton)`
  display: flex;
`;
